import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useCompanies, useDeleteCompany } from '../../hooks/unified/useCompaniesQueries';
import { useToast } from '../../contexts/ToastContext';
import { useViewport } from '../../hooks/useViewport';
import { env } from '../../config/env';
import type { Company } from '../../services/unified/types';

export function AdminCompaniesPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  useViewport(); // For responsive CSS behavior
  const { data: companies = [], isLoading } = useCompanies();
  const deleteCompanyMutation = useDeleteCompany();
  
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCompanies = companies.filter((company: Company) =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.industry && company.industry.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateCompany = () => {
    navigate('/admin/companies/new');
  };

  const handleViewCompany = (companyId: string) => {
    navigate(`/admin/companies/${companyId}`);
  };

  const handleDeleteCompany = async (companyId: string, companyName: string) => {
    if (!confirm(`Are you sure you want to delete "${companyName}"? This will also delete all associated assets.`)) {
      return;
    }

    try {
      await deleteCompanyMutation.mutateAsync(companyId);
      showSuccess('Company deleted successfully');
    } catch (error) {
      showError('Failed to delete company');
    }
  };

  // Helper function to get full logo URL
  const getLogoUrl = (logoUrl: string | null | undefined): string | null => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    return `${env.API_BASE_URL}${logoUrl}`;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-0 sm:p-0">
        {/* Header - Mobile optimized */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Companies</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Manage company profiles and assets
            </p>
          </div>
          <button
            onClick={handleCreateCompany}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium min-h-[44px] w-full sm:w-auto"
          >
            + Add Company
          </button>
        </div>

        {/* Search */}
        <div className="mb-4 sm:mb-6">
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
          />
        </div>

        {/* Companies Grid */}
        {filteredCompanies.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search' : 'Get started by creating a new company'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <button
                  onClick={handleCreateCompany}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  + Add Company
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {filteredCompanies.map((company: Company) => (
              <div
                key={company.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewCompany(company.id)}
              >
                <div className="p-4 sm:p-6">
                  {/* Logo and Name - Horizontal on mobile */}
                  <div className="flex items-center gap-3 sm:block">
                    <div className="h-12 w-12 sm:h-16 sm:w-16 sm:mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                      {getLogoUrl(company.default_logo_url) ? (
                        <img
                          src={getLogoUrl(company.default_logo_url)!}
                          alt={company.name}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.parentElement?.querySelector('.logo-fallback');
                            if (fallback) fallback.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`logo-fallback flex items-center justify-center ${getLogoUrl(company.default_logo_url) ? 'hidden' : ''}`}>
                        <svg className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    </div>

                    {/* Company Info - Mobile inline */}
                    <div className="flex-1 min-w-0 sm:hidden">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{company.name}</h3>
                      {company.industry && (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 rounded mt-1">
                          {company.industry}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop Company Info */}
                  <div className="hidden sm:block">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{company.name}</h3>
                    
                    {company.industry && (
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded mb-2">
                        {company.industry}
                      </span>
                    )}

                    {company.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {company.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        {company.company_size && (
                          <span>👥 {company.company_size}</span>
                        )}
                        {company.founded_year && (
                          <span>📅 {company.founded_year}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile: Compact meta */}
                  <div className="sm:hidden mt-3 flex items-center gap-3 text-xs text-gray-500">
                    {company.company_size && <span>👥 {company.company_size}</span>}
                    {company.founded_year && <span>📅 {company.founded_year}</span>}
                  </div>

                  {/* Actions */}
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCompany(company.id);
                      }}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium min-h-[44px] flex items-center"
                    >
                      View
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCompany(company.id, company.name);
                      }}
                      className="text-red-600 hover:text-red-700 text-sm font-medium min-h-[44px] flex items-center"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
